/* Mobile reading clearance without reparenting Klaviyo's React-owned DOM. */
(() => {
  if (window.__gapMobileWidgetInline) return;
  window.__gapMobileWidgetInline = true;
  const mobile = window.matchMedia('(max-width: 767px), (max-width: 1023px) and (max-height: 500px) and (hover: none) and (pointer: coarse)');
  const containerSelector = '.kl-hub-launcher-container';
  const cardSelector = '.kl-hub-launcher[class*="AnimatedHubWidget-addToCart-"]';
  const properties = ['--gap-widget-inline-top', '--gap-widget-inline-left', '--gap-widget-inline-width'];
  let container = null;
  let slot = null;
  let reservedHeight = 0;
  let queued = false;
  let closedFrom = null;

  function property(element, name, value) {
    if (element.style.getPropertyValue(name) !== value) element.style.setProperty(name, value);
  }
  function schedule() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(sync);
  }
  const widgetChanges = new MutationObserver(schedule);
  const contentChanges = new MutationObserver(records => {
    if (records.some(record => record.target !== slot)) schedule();
  });
  const resize = typeof ResizeObserver === 'function' ? new ResizeObserver(schedule) : null;

  function release() {
    widgetChanges.disconnect();
    contentChanges.disconnect();
    if (container) {
      resize?.unobserve(container);
      container.removeAttribute('data-gap-mobile-inline');
      properties.forEach(name => container.style.removeProperty(name));
    }
    slot?.remove();
    container = null;
    slot = null;
    reservedHeight = 0;
    closedFrom = null;
  }
  function makeSlot(main) {
    const element = document.createElement('div');
    element.className = 'gap-mobile-widget-slot';
    element.setAttribute('aria-hidden', 'true');
    // Product details remain below the widget, rather than behind it.
    const accordion = main.querySelector('.product-accordion');
    const buy = main.querySelector('.product-buy-button');
    const title = main.querySelector('.customer-service-content__page-title, .customer-service-cover__heading-wrap, .main-page__title, .shopify-policy__title');
    if (accordion) accordion.before(element);
    else if (buy) buy.after(element);
    else if (title) title.after(element);
    else main.append(element);
    return element;
  }
  function containingOrigin(element) {
    const parent = element.offsetParent;
    if (!parent || parent === document.documentElement ||
      (parent === document.body && getComputedStyle(parent).position === 'static')) {
      return { left: 0, top: 0 };
    }
    const rect = parent.getBoundingClientRect();
    return {
      left: rect.left + window.scrollX + parent.clientLeft - parent.scrollLeft,
      top: rect.top + window.scrollY + parent.clientTop - parent.scrollTop,
    };
  }
  function restoreClosedCardFocus(mode) {
    if (!closedFrom || mode !== 'chat') return;
    const active = document.activeElement;
    if (active && active !== document.body && active !== document.documentElement &&
      active.isConnected && active !== closedFrom && !closedFrom.contains(active)) {
      closedFrom = null;
      return;
    }
    const launcher = container.querySelector('.kl-hub-launcher');
    if (!launcher || !launcher.getClientRects().length || launcher.disabled ||
      launcher.closest('[inert], [aria-hidden="true"]') ||
      getComputedStyle(launcher).visibility === 'hidden') return;
    closedFrom = null;
    launcher.focus({ preventScroll: true });
  }
  function sync() {
    queued = false;
    const main = document.getElementById('MainContent');
    const next = document.querySelector(containerSelector);
    if (!mobile.matches || !main || !next) {
      if (container || slot) release();
      return;
    }
    if (container !== next) {
      release();
      container = next;
      widgetChanges.observe(container, { subtree: true, childList: true, attributes: true, attributeFilter: ['class', 'style', 'hidden', 'aria-hidden'] });
      contentChanges.observe(main, { subtree: true, attributes: true, attributeFilter: ['class', 'style', 'hidden'] });
      resize?.observe(container);
      resize?.observe(main);
    }
    if (!slot?.isConnected) slot = makeSlot(main);
    const mode = container.querySelector(cardSelector) ? 'card' : 'chat';
    if (container.getAttribute('data-gap-mobile-inline') !== mode) container.setAttribute('data-gap-mobile-inline', mode);
    const slotRect = slot.getBoundingClientRect();
    if (slotRect.width <= 0) return;
    property(container, '--gap-widget-inline-width', `${slotRect.width}px`);
    const rect = container.getBoundingClientRect();
    const height = Math.max(container.offsetHeight, rect.height);
    // Keep the largest observed slot until navigation/desktop mode. Closing the
    // card or opening its drawer must not pull reading text up under the user.
    if (height > 0) reservedHeight = Math.max(reservedHeight, Math.ceil(height) + 16);
    property(slot, '--gap-widget-slot-height', `${reservedHeight}px`);
    const origin = containingOrigin(container);
    const left = slotRect.left + window.scrollX + (mode === 'chat' ? Math.max(0, slotRect.width - rect.width) : 0);
    property(container, '--gap-widget-inline-left', `${left - origin.left}px`);
    property(container, '--gap-widget-inline-top', `${slotRect.top + window.scrollY + 8 - origin.top}px`);
    restoreClosedCardFocus(mode);
  }

  document.addEventListener('click', event => {
    const close = event.target.closest?.('[aria-label="Close dialog"]');
    if (mobile.matches && close && container?.contains(close) && close.closest(cardSelector)) closedFrom = close;
  }, true);
  document.addEventListener('focusin', event => {
    if (!mobile.matches || !container?.contains(event.target) || !slot?.isConnected) return;
    if (!event.target.closest('.kl-hub-launcher') ||
      event.target.closest('[role="dialog"], [role="listbox"], [role="menu"], [role="option"], [role="menuitem"], [aria-modal="true"]')) return;
    const rect = event.target.getBoundingClientRect();
    const header = document.getElementById('header-group')?.getBoundingClientRect();
    const top = Math.max(0, header?.bottom || 0) + 8;
    if (rect.top < top || rect.bottom > window.innerHeight - 8) {
      slot.scrollIntoView({ block: 'center', behavior: 'instant' });
    }
  });
  new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true });
  resize?.observe(document.body);
  resize?.observe(document.getElementById('header-group') || document.body);
  mobile.addEventListener('change', schedule);
  window.addEventListener('resize', schedule, { passive: true });
  window.addEventListener('pageshow', schedule);
  document.addEventListener('load', schedule, true);
  document.fonts?.ready.then(schedule);
  schedule();
})();
